import { Select } from "../../components/Formulario";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { colorParaUsuario, COLOR_POR_NIVEL_CARGA, resolverColorSemantico } from "../../lib/colorSemantico";
import { calcularNivelCarga } from "./colaTrabajo";

export interface CargaTrabajoAbogado {
  id: string;
  etiqueta: string;
  audiencias: number;
  escritos: number;
  tareas: number;
  vencimientos: number;
  expedientesActivos: number;
  honorariosPendientes: number;
}

const CAMPOS_ORDENABLES: { clave: keyof CargaTrabajoAbogado; etiqueta: string }[] = [
  { clave: "audiencias", etiqueta: "Audiencias" },
  { clave: "escritos", etiqueta: "Escritos" },
  { clave: "tareas", etiqueta: "Tareas" },
  { clave: "vencimientos", etiqueta: "Vencimientos" },
  { clave: "expedientesActivos", etiqueta: "Expedientes activos" },
  { clave: "honorariosPendientes", etiqueta: "Honorarios pendientes" },
];

// Vista secundaria (spec §9), sólo para expedientes.ver_todos — agrega
// ÚNICAMENTE una lectura visual (Baja/Media/Alta/Sobrecargada) derivada de
// los mismos números que ya mostraba la tabla, reusando colorSemantico.ts.
// Los números originales siguen visibles al lado del badge: no se presenta
// como medición objetiva de capacidad, sólo como semáforo de un vistazo.
export function CargaEquipoTabla({
  filas,
  orden,
  onCambiarOrden,
}: {
  filas: CargaTrabajoAbogado[];
  orden: keyof CargaTrabajoAbogado;
  onCambiarOrden: (clave: keyof CargaTrabajoAbogado) => void;
}) {
  const ordenadas = [...filas].sort((a, b) => (b[orden] as number) - (a[orden] as number));

  const columnas: ColumnaTabla<CargaTrabajoAbogado>[] = [
    {
      encabezado: "Abogado",
      render: (f) => (
        <span className="inline-flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${resolverColorSemantico(colorParaUsuario(f.id)).punto}`} />
          {f.etiqueta}
        </span>
      ),
    },
    {
      encabezado: "Carga",
      render: (f) => {
        const total = f.audiencias + f.escritos + f.tareas + f.vencimientos;
        const nivel = calcularNivelCarga(total);
        return (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${resolverColorSemantico(COLOR_POR_NIVEL_CARGA[nivel]).chip}`}>
            {nivel}
          </span>
        );
      },
    },
    { encabezado: "Audiencias", render: (f) => f.audiencias },
    { encabezado: "Escritos", render: (f) => f.escritos },
    { encabezado: "Tareas", render: (f) => f.tareas },
    { encabezado: "Vencimientos", render: (f) => f.vencimientos },
    { encabezado: "Expedientes activos", render: (f) => f.expedientesActivos },
    { encabezado: "Honorarios pendientes", render: (f) => f.honorariosPendientes },
  ];

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <span className="text-sm text-texto-secundario">Ordenar por</span>
        <Select value={orden} onChange={(e) => onCambiarOrden(e.target.value as keyof CargaTrabajoAbogado)} className="w-56">
          {CAMPOS_ORDENABLES.map((c) => (
            <option key={c.clave} value={c.clave}>
              {c.etiqueta}
            </option>
          ))}
        </Select>
      </div>
      <div className="overflow-x-auto">
        <Tabla columnas={columnas} filas={ordenadas} claveFila={(f) => f.id} tituloVacio="Sin datos de carga de trabajo" />
      </div>
    </div>
  );
}
